import clsx from "clsx";
import { useEffect, useState, type MouseEvent } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { isMobileViewport } from "../hooks/useIsMobileViewport.ts";
import { helpers } from "../util/helpers.ts";
import useClickable from "../hooks/useClickable.tsx";
import type { View, Game } from "../../../../common/types.ts";
import getWinner from "../../../../common/getWinner.ts";
import formatScoreWithShootout from "../../../../common/formatScoreWithShootout.ts";
import { BoxScoreWrapper } from "../components/BoxScoreWrapper.tsx";
import { BoxScoreRow } from "../components/BoxScoreRow.tsx";
import i18n from "../util/i18n.ts";
import { formatOvertimeSummary } from "../util/basketballLabels.ts";
import { TeamAbbrevText } from "../components/TeamAbbrevLink.tsx";
import { TeamLogoInline } from "../components/TeamLogoInline.tsx";
import { useLocalPartial } from "../util/local.ts";
import { getGameLogConclusion } from "./gamesTemplate.ts";
import { MobileContentHero, MobileEntityRow, MobileSection } from "../mobile/mobilePrimitives.tsx";
import { MobileP3ShareCard } from "../mobile/MobileP3ShareExportActions.tsx";
import {
  GAME_ATTRIBUTION_DEFAULTS,
  getGameAttributionVariant,
} from "../mobile/gameAttributionText.ts";
import { getCol } from "../util/getCol.ts";
import {
  carryGameLogDetailReturnState,
  createGameLogRowMobileHref,
  resolveGameLogDetailCloseContext,
} from "./gameLogDetail.ts";

const StatsRow = ({ p, ...props }: { i: number; p: any; season: number }) => {
  const { clicked, toggleClicked } = useClickable();

  const classes = clsx({
    "table-warning": clicked,
  });
  return <BoxScoreRow className={classes} onClick={toggleClicked} p={p} {...props} />;
};

const findPrevNextGids = (games: Game[], currentGid: number) => {
  let prevGid;
  let nextGid;
  let currentGidInList = false;

  for (const [i, game] of games.entries()) {
    if (game.gid === currentGid) {
      currentGidInList = true;
      nextGid = games[i - 1]?.gid;
      prevGid = games[i + 1]?.gid;
      break;
    }
  }

  return { currentGidInList, prevGid, nextGid };
};

const gameResultLabel = (result: "loss" | "tie" | "win") => {
  switch (result) {
    case "win":
      return i18n.t("gameLog.resultWin", { ns: "ui", defaultValue: "Win" });
    case "loss":
      return i18n.t("gameLog.resultLoss", { ns: "ui", defaultValue: "Loss" });
    default:
      return i18n.t("gameLog.resultTie", { ns: "ui", defaultValue: "Tie" });
  }
};

const getGameResultKindForTid = (game: Game, tid: number) => {
  const user = game.teams.findIndex((team) => team.tid === tid);

  if (user < 0) {
    return "tie";
  }

  const other = user === 0 ? 1 : 0;
  const winner = getWinner(game.teams);

  return winner === user ? "win" : winner === other ? "loss" : "tie";
};

const differenceLabel = (
  key: NonNullable<ReturnType<typeof getGameLogConclusion>>["keyDifferences"][number]["key"],
) => {
  switch (key) {
    case "rebounds":
      return i18n.t("gameLog.diffRebounds", {
        ns: "ui",
        defaultValue: "Rebounds",
      });
    case "turnovers":
      return i18n.t("gameLog.diffTurnovers", {
        ns: "ui",
        defaultValue: "Turnovers",
      });
    case "freeThrows":
      return i18n.t("gameLog.diffFreeThrows", {
        ns: "ui",
        defaultValue: "Free throws",
      });
    default:
      return i18n.t("gameLog.diffPoints", {
        ns: "ui",
        defaultValue: "Points",
      });
  }
};

const formatDifference = (value: number) => (value > 0 ? `+${value}` : String(value));

const getGameLogUrl = ({
  abbrev,
  gid,
  season,
  tid,
}: {
  abbrev: string;
  gid?: number;
  season: number;
  tid: number;
}) =>
  helpers.leagueUrl(["game_log", abbrev === "special" ? abbrev : `${abbrev}_${tid}`, season, gid]);

const getGameLocationLabel = ({ home, neutralSite }: { home: boolean; neutralSite?: boolean }) => {
  if (neutralSite) {
    return i18n.t("gameLog.neutralGame", {
      ns: "ui",
      defaultValue: "Neutral site",
    });
  }

  return home
    ? i18n.t("gameLog.homeGame", { ns: "ui", defaultValue: "Home" })
    : i18n.t("gameLog.awayGame", { ns: "ui", defaultValue: "Away" });
};

const getPrimaryDifference = (
  keyDifferences: NonNullable<ReturnType<typeof getGameLogConclusion>>["keyDifferences"],
) =>
  [...keyDifferences]
    .filter((diff) => diff.key !== "points")
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))[0] ?? keyDifferences[0];

export const NoGamesMessage = ({ warnAboutDelete }: { warnAboutDelete: boolean }) => (
  <div className="alert alert-info d-inline-block" style={{ maxWidth: 600 }}>
    {i18n.t("gameLog.noGamesFound", {
      ns: "ui",
      defaultValue: "No games found for this season.",
    })}
    {warnAboutDelete ? (
      <>
        {" "}
        {i18n.t("gameLog.boxScoresDeleteNote", {
          ns: "ui",
          defaultValue:
            "By default, box scores from old seasons are automatically deleted after 2 seasons.",
        })}
      </>
    ) : null}
  </div>
);

const GamesList = ({
  abbrev,
  currentSeason,
  gid,
  gamesList,
  season,
  tid,
}: {
  abbrev: string;
  currentSeason: number;
  gamesList: View<"gameLog">["gamesList"];
  gid?: number;
  season: number;
  tid: number;
}) => {
  // C2: opponent logos on the matchup rows (C1 scoreboard-card precedent —
  // logos come from the client teamInfoCache, no view change needed).
  const { teamInfoCache } = useLocalPartial(["teamInfoCache"]);
  const gameLogReturnLabel = i18n.t("gameLog.returnToGameLog", {
    ns: "ui",
    defaultValue: "Return to game log",
  });
  const seedGameLogReturn = (event: MouseEvent<HTMLAnchorElement>, detailHref: string) => {
    if (
      typeof window === "undefined" ||
      !isMobileViewport() ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.currentTarget.href = createGameLogRowMobileHref({
      detailHref,
      returnHref: `${window.location.pathname}${window.location.search}`,
      returnLabel: gameLogReturnLabel,
      scrollY: window.scrollY,
    });
  };

  if (season < currentSeason && gamesList.games.length === 0) {
    return <NoGamesMessage warnAboutDelete />;
  }

  const recentGames = gamesList.games.slice(0, 5);
  const selectedGame =
    gid !== undefined ? gamesList.games.find((game) => game.gid === gid) : undefined;
  const visibleGames =
    selectedGame && !recentGames.some((game) => game.gid === selectedGame.gid)
      ? [selectedGame, ...recentGames.slice(0, 4)]
      : recentGames;
  const visibleGids = new Set(visibleGames.map((game) => game.gid));
  const hiddenGames = gamesList.games.filter((game) => !visibleGids.has(game.gid));
  const hiddenGameCount = hiddenGames.length;

  const renderGame = (gm: Game) => {
    const home = gm.teams[0].tid === tid;
    const user = home ? 0 : 1;
    const other = home ? 1 : 0;
    const winner = getWinner(gm.teams);
    const resultKind = winner === user ? "win" : winner === other ? "loss" : "tie";
    const rowConclusion = getGameLogConclusion({ boxScore: gm, tid });
    const primaryDifference = rowConclusion
      ? getPrimaryDifference(rowConclusion.keyDifferences)
      : undefined;
    const overtimeText = formatOvertimeSummary(gm.overtimes);
    const overtimes = overtimeText === "" ? "" : ` ${overtimeText}`;
    const scoreText = `${formatScoreWithShootout(gm.teams[user], gm.teams[other])}${overtimes}`;
    const oppAbbrev = gamesList.abbrevs[gm.teams[other].tid];
    const dayLabel =
      gm.day !== undefined
        ? i18n.t("gameLog.dayLabel", {
            ns: "ui",
            day: gm.day,
            defaultValue: "Day {{day}}",
          })
        : undefined;
    const contextItems = [
      scoreText,
      dayLabel,
      getGameLocationLabel({ home, neutralSite: gm.neutralSite }),
    ].filter((item): item is string => item !== undefined && item !== "");
    const rowReason = primaryDifference
      ? i18n.t("gameLog.rowReason", {
          ns: "ui",
          defaultValue: "Swing: {{stat}} {{value}}",
          stat: differenceLabel(primaryDifference.key),
          value: formatDifference(primaryDifference.value),
        })
      : undefined;
    const url = getGameLogUrl({ abbrev, gid: gm.gid, season, tid });

    return (
      <a
        className={clsx("d-block text-reset text-decoration-none", {
          "bg-info-subtle": gm.gid === gid,
        })}
        href={url}
        key={gm.gid}
        onClick={(event) => seedGameLogReturn(event, url)}
        data-game-log-detail-entry=""
      >
        <MobileEntityRow
          className="game-log-row-card"
          data-game-log-click-target=""
          data-game-log-row={gm.gid}
          detail={rowReason ? <span data-game-log-row-reason="">{rowReason}</span> : undefined}
          rank={
            <span
              className={clsx(
                "badge",
                resultKind === "win"
                  ? "text-bg-success"
                  : resultKind === "loss"
                    ? "text-bg-secondary"
                    : "text-bg-warning",
                gm.forceWin !== undefined && "alert-forced-result",
              )}
              data-game-log-row-result={resultKind}
            >
              {gameResultLabel(resultKind)}
            </span>
          }
          meta={<span data-game-log-row-context="">{contextItems.join(" · ")}</span>}
          title={
            <span className="d-flex align-items-center gap-1" data-game-log-opponent-matchup="">
              {(() => {
                const oppTid = gm.teams[other].tid;
                const oppInfo = oppTid >= 0 ? teamInfoCache[oppTid] : undefined;
                return (
                  <TeamLogoInline
                    alt=""
                    className="game-log-opponent-logo flex-shrink-0"
                    imgURL={oppInfo?.imgURLSmall ?? oppInfo?.imgURL}
                    size={16}
                  />
                );
              })()}
              <span>
                {gm.neutralSite || home ? "" : "@"}
                {oppAbbrev ? (
                  <TeamAbbrevText tid={gm.teams[other].tid} abbrev={oppAbbrev} />
                ) : (
                  i18n.t("common.unavailable", {
                    ns: "ui",
                    defaultValue: "Unavailable",
                  })
                )}
              </span>
            </span>
          }
        />
      </a>
    );
  };

  return (
    <div
      className="game-log-list"
      data-game-log-compact-list=""
      data-game-log-recent-cards=""
      data-game-log-return-position={gid ?? ""}
    >
      {gamesList.tid !== tid ? (
        <p className="text-body-secondary">
          {i18n.t("common.loading", { ns: "ui", defaultValue: "Loading..." })}
        </p>
      ) : (
        <>
          {visibleGames.map(renderGame)}
          {hiddenGameCount > 0 ? (
            <details
              className="mt-2"
              data-game-log-full-list-depth=""
              data-native-details-host="game-log-full-list"
            >
              <summary className="btn btn-outline-secondary btn-sm">
                {i18n.t("gameLog.moreRecentGames", {
                  ns: "ui",
                  count: hiddenGameCount,
                  defaultValue: "{{count}} more game(s)",
                })}
              </summary>
              <div className="mt-2" data-game-log-full-list-content="">
                {hiddenGames.map(renderGame)}
              </div>
            </details>
          ) : null}
        </>
      )}
    </div>
  );
};

const GameLog = ({
  abbrev,
  attribution,
  boxScore,
  currentSeason,
  gamesList,
  season,
  tid,
}: View<"gameLog">) => {
  const dropdownTeamsKey = "teamsAndSpecial";

  useTitleBar({
    title: i18n.t("gameLog.title", { ns: "ui", defaultValue: "Game Log" }),
    dropdownView: "game_log",
    dropdownFields: {
      [dropdownTeamsKey]: abbrev,
      seasons: season,
    },
    dropdownCustomURL: (fields) => {
      return helpers.leagueUrl([
        "game_log",
        fields[dropdownTeamsKey],
        fields.seasons,
        boxScore.gid,
      ]);
    },
  });

  const { currentGidInList, nextGid, prevGid } = findPrevNextGids(gamesList.games, boxScore.gid);

  const noGamesAndNoBoxScore =
    season < currentSeason && gamesList.games.length === 0 && boxScore.gid < 0;
  const isHistoricalSeason = season < currentSeason;
  const gameConclusion = getGameLogConclusion({ boxScore, tid });
  const keyDifferences = gameConclusion?.keyDifferences.slice(0, 3) ?? [];

  // G3-1: post-game "your decision" credit line for a traded-for / signed new
  // arrival. Worker-derived (only the user's own team, current season); a single
  // best-performer line, never a stack.
  const attributionVariant = attribution ? getGameAttributionVariant(attribution) : undefined;
  const attributionText =
    attribution && attributionVariant
      ? i18n.t(`gameLog.attribution.${attributionVariant}`, {
          ns: "ui",
          defaultValue: GAME_ATTRIBUTION_DEFAULTS[attributionVariant],
          name: attribution.name,
          stats: attribution.stats
            .map(({ stat, value }) => `${value} ${getCol(`stat:${stat}`).title}`)
            .join(" / "),
        })
      : undefined;
  const teamRouteParam =
    tid >= 0 ? (abbrev === "special" ? abbrev : `${abbrev}_${tid}`) : undefined;
  const latestGame = gamesList.tid === tid ? gamesList.games[0] : undefined;
  const latestGameUrl =
    latestGame !== undefined
      ? getGameLogUrl({ abbrev, gid: latestGame.gid, season, tid })
      : undefined;
  const recentTeamTid = gamesList.tid >= 0 ? gamesList.tid : tid;
  const recentTeamGames = teamRouteParam ? gamesList.games.slice(0, 5) : [];
  const recentTeamTrend = recentTeamGames.reduce(
    (trend, game) => {
      const result = getGameResultKindForTid(game, recentTeamTid);
      trend[result] += 1;
      return trend;
    },
    { loss: 0, tie: 0, win: 0 },
  );
  const recentTeamTrendText =
    recentTeamGames.length > 0
      ? i18n.t("gameLog.teamRecentFive", {
          ns: "ui",
          count: recentTeamGames.length,
          defaultValue: "Recent {{count}}: {{wins}}W {{losses}}L {{ties}}T",
          losses: recentTeamTrend.loss,
          ties: recentTeamTrend.tie,
          wins: recentTeamTrend.win,
        })
      : i18n.t("gameLog.teamRecentFiveEmpty", {
          ns: "ui",
          defaultValue: "No recent games for this filter.",
        });
  const keyDifferenceText =
    keyDifferences
      .map((diff) => `${differenceLabel(diff.key)} ${formatDifference(diff.value)}`)
      .join(", ") ||
    i18n.t("gameLog.p3ShareNoSwingStats", {
      ns: "ui",
      defaultValue: "No swing stats",
    }) ||
    "No swing stats";
  const gameRecapShareTitle =
    i18n.t("gameLog.p3ShareTitle", {
      ns: "ui",
      defaultValue: "{{season}} Game Recap",
      season,
    }) || `${season} Game Recap`;
  const gameRecapShareText = gameConclusion
    ? i18n.t("gameLog.p3ShareText", {
        ns: "ui",
        defaultValue:
          "{{result}} {{teamScore}}-{{opponentScore}}. Key player: {{topPlayer}}. Swings: {{keyDifferences}}.",
        keyDifferences: keyDifferenceText,
        opponentScore: gameConclusion.score[1],
        result: gameResultLabel(gameConclusion.result),
        teamScore: gameConclusion.score[0],
        topPlayer:
          gameConclusion.topPlayer?.name ??
          i18n.t("gameLog.p3ShareNoKeyPlayer", {
            ns: "ui",
            defaultValue: "No key player",
          }),
      }) ||
      `${gameResultLabel(gameConclusion.result)} ${gameConclusion.score[0]}-${
        gameConclusion.score[1]
      }. Key player: ${gameConclusion.topPlayer?.name ?? "No key player"}. Swings: ${
        keyDifferenceText
      }.`
    : i18n.t("gameLog.p3ShareFallbackText", {
        ns: "ui",
        defaultValue: "Game recap and box score are available.",
      }) || "Game recap and box score are available.";
  const gameRecapShareUrl = getGameLogUrl({ abbrev, gid: boxScore.gid, season, tid });
  const boxScoreListHref = getGameLogUrl({ abbrev, season, tid });
  const boxScoreListLabel = i18n.t("gameLog.contextBackToList", {
    ns: "ui",
    defaultValue: "Back to game list",
  });
  const [boxScoreCloseContext, setBoxScoreCloseContext] = useState(() =>
    resolveGameLogDetailCloseContext({
      fallbackHref: boxScoreListHref,
      fallbackLabel: boxScoreListLabel,
      search: "",
    }),
  );

  useEffect(() => {
    if (boxScore.gid < 0 || typeof window === "undefined") {
      return;
    }

    setBoxScoreCloseContext(
      resolveGameLogDetailCloseContext({
        fallbackHref: boxScoreListHref,
        fallbackLabel: boxScoreListLabel,
        search: window.location.search,
      }),
    );
  }, [boxScore.gid, boxScoreListHref, boxScoreListLabel]);
  const carryBoxScoreReturnToSibling = (event: MouseEvent<HTMLDivElement>) => {
    if (
      typeof window === "undefined" ||
      !isMobileViewport() ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const siblingGameLink = event.target.closest<HTMLAnchorElement>(".box-score-game-nav a");
    if (
      siblingGameLink === null ||
      siblingGameLink.classList.contains("disabled") ||
      !event.currentTarget.contains(siblingGameLink)
    ) {
      return;
    }

    const bareHref = siblingGameLink.getAttribute("href");
    if (!bareHref) {
      return;
    }

    siblingGameLink.href = carryGameLogDetailReturnState({
      detailHref: bareHref,
      search: window.location.search,
    });
  };

  return (
    <div className="game-log-page-root" data-v5-game-log-profile="">
      {teamRouteParam ? (
        <>
          <section className="d-flex flex-wrap align-items-center gap-2 mb-2">
            {/* G2b-2 carried fix: the Team / Season context chips duplicated the
                TitleBar dropdown filters (法则二). The team/season bearing anchors
                are rehung onto the non-duplicate recent-form record badge, which
                carries information (W-L trend) that the TitleBar does not. */}
            <span className="badge text-bg-light border">{recentTeamTrendText}</span>
            {isHistoricalSeason ? (
              <span className="badge text-bg-secondary">
                {i18n.t("gameLog.historyReadonly", {
                  ns: "ui",
                  defaultValue: "Read-only archive",
                })}
              </span>
            ) : null}
            <a className="btn btn-link btn-sm px-0" href={helpers.leagueUrl(["game_log"])}>
              {i18n.t("gameLog.returnAllGames", {
                ns: "ui",
                defaultValue: "All league games",
              })}
            </a>
            {isHistoricalSeason ? (
              <a
                className="btn btn-outline-secondary btn-sm"
                href={helpers.leagueUrl(["game_log", teamRouteParam])}
              >
                {i18n.t("gameLog.p2s02ReturnCurrentSeason", {
                  ns: "ui",
                  defaultValue: "Current season",
                })}
              </a>
            ) : null}
          </section>
          <nav
            aria-label={i18n.t("gameLog.contextLinksLabel", {
              ns: "ui",
              defaultValue: "Game log actions",
            })}
            className="more-links-single"
            data-game-log-context-links=""
          >
            <a href={helpers.leagueUrl(["game_log", teamRouteParam, season])}>
              {boxScore.gid >= 0
                ? i18n.t("gameLog.contextBackToList", {
                    ns: "ui",
                    defaultValue: "Back to game list",
                  })
                : i18n.t("gameLog.contextTeamList", {
                    ns: "ui",
                    defaultValue: "Team game list",
                  })}
            </a>{" "}
            ·{" "}
            <a href={helpers.leagueUrl(["daily_schedule", "team", teamRouteParam])}>
              {i18n.t("gameLog.contextSchedule", {
                ns: "ui",
                defaultValue: "Open team schedule",
              })}
            </a>
          </nav>
        </>
      ) : null}

      {noGamesAndNoBoxScore ? (
        <NoGamesMessage warnAboutDelete />
      ) : (
        <div className="game-log-page mt-2">
          <div className="row g-3 align-items-start">
            <div className="col-12">
              {boxScore.gid >= 0 ? (
                <div
                  className="game-log-fullscreen-detail"
                  data-mobile-box-score-fullscreen=""
                  data-mobile-box-score-sibling-return-capture=""
                  onClickCapture={carryBoxScoreReturnToSibling}
                >
                  <header
                    className="game-log-fullscreen-close-bar"
                    data-mobile-box-score-close-bar=""
                  >
                    <a
                      aria-label={i18n.t("gameLog.closeBoxScoreAria", {
                        ns: "ui",
                        defaultValue: "Close box score and return to {{destination}}",
                        destination: boxScoreCloseContext.label,
                      })}
                      className="btn btn-outline-secondary game-log-fullscreen-close"
                      data-mobile-box-score-close=""
                      data-mobile-box-score-close-source={boxScoreCloseContext.source}
                      href={boxScoreCloseContext.href}
                    >
                      <span aria-hidden="true">×</span>
                      <span>
                        {i18n.t("gameLog.closeBoxScore", {
                          ns: "ui",
                          defaultValue: "Close",
                        })}
                      </span>
                      <span className="text-body-secondary">{boxScoreCloseContext.label}</span>
                    </a>
                  </header>
                  <div
                    className="game-log-fullscreen-content"
                    data-mobile-box-score-fullscreen-content=""
                  >
                    {attributionText ? (
                      <div
                        className="game-log-attribution alert alert-success py-2 px-3 mb-3"
                        data-game-log-attribution={attributionVariant}
                      >
                        {attributionText}
                      </div>
                    ) : null}
                    <MobileSection
                      data-advanced-box-score-tabs=""
                      data-mobile-box-score-summary-first=""
                    >
                      <BoxScoreWrapper
                        abbrev={abbrev}
                        boxScore={boxScore}
                        currentGidInList={currentGidInList}
                        nextGid={nextGid}
                        prevGid={prevGid}
                        showNextPrev
                        playerTableDefaultOpen={false}
                        sportState={undefined}
                        tid={tid}
                        Row={StatsRow}
                      />
                    </MobileSection>
                    <details
                      className="game-log-share-card mt-3"
                      data-game-log-share-recap=""
                      data-native-details-host="game-log-share-recap"
                    >
                      <summary className="btn btn-outline-secondary btn-sm">
                        {i18n.t("gameLog.p3ShareSummary", {
                          ns: "ui",
                          defaultValue: "Share recap",
                        })}
                      </summary>
                      <MobileP3ShareCard
                        className="mt-2"
                        kind="gameRecap"
                        payload={{
                          text: gameRecapShareText,
                          title: gameRecapShareTitle,
                          url: gameRecapShareUrl,
                        }}
                      />
                    </details>
                    <details
                      className="game-log-related-list mt-3"
                      data-game-log-related-list=""
                      data-native-details-host="game-log-related-games"
                    >
                      <summary className="btn btn-outline-secondary btn-sm">
                        {i18n.t("gameLog.otherRecentGames", {
                          ns: "ui",
                          defaultValue: "Other recent games",
                        })}
                      </summary>
                      <div className="mt-2">
                        <GamesList
                          abbrev={abbrev}
                          currentSeason={currentSeason}
                          gamesList={gamesList}
                          gid={boxScore.gid}
                          season={season}
                          tid={tid}
                        />
                      </div>
                    </details>
                  </div>
                </div>
              ) : (
                <MobileContentHero
                  action={
                    latestGameUrl !== undefined ? (
                      <a
                        className="btn btn-primary"
                        data-game-log-latest-recap-action=""
                        href={latestGameUrl}
                      >
                        {i18n.t("gameLog.openLatestRecap", {
                          ns: "ui",
                          defaultValue: "View latest box score",
                        })}
                      </a>
                    ) : undefined
                  }
                  className="mb-3"
                  data-game-log-empty-selection=""
                  data-v5-game-log-recent-first-screen=""
                  eyebrow={i18n.t("gameLog.mobileReviewEyebrow", {
                    ns: "ui",
                    defaultValue: "Postgame review",
                  })}
                >
                  <GamesList
                    abbrev={abbrev}
                    currentSeason={currentSeason}
                    gamesList={gamesList}
                    gid={boxScore.gid}
                    season={season}
                    tid={tid}
                  />
                </MobileContentHero>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameLog;
